import { Box, CircularProgress, Typography } from '@mui/material';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, AuthenticatedLayoutWithNavbar, getMessageFromError } from '@pro4-erp/core-web';
import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  useDoesUserHaveAnyAuditInvitations,
  getClientPartner,
  useDoesUserHaveAnyAuditProposals,
  useDoesUserHaveAnyAuditCompeteces,
  useAverageRate,
  useDoesUserHaveAnyVisitRepReviews,
} from '@/common';
import { useAuthStore } from '../hooks/useAuthStore';
import { impl } from '@/main';
import { getNextCloudUrlInstructions } from '@/common/api/getNextCloudUrlInstructions';

type NavbarItem = {
  title: string;
  path: string;
  isActive: boolean;
  showNotification?: boolean;
  color: string;
};

const AuthenticatedLayout = () => {
  const { t } = useTranslation();
  const authStore = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // const doesUserHaveAnyReminders = useDoesUserHaveAnyReminders();
  const doesUserHaveAnyVisitRepReviews = useDoesUserHaveAnyVisitRepReviews(
    authStore?.user?.osv.type === 'Auditor'
  );
  const doesUserHaveAnyAuditInvitations = useDoesUserHaveAnyAuditInvitations(
    authStore?.user?.osv.type === 'Auditor'
  );
  const doesUserHaveAnyProposals = useDoesUserHaveAnyAuditProposals(
    authStore?.user?.osv.type === 'Auditor'
  );
  const doesUserHaveAnyCompetences = useDoesUserHaveAnyAuditCompeteces(
    authStore?.user?.osv.type === 'Auditor'
  );

  const averageRate = useAverageRate(authStore?.user?.osv.type === 'Auditor');

  const query = useQuery<string, ApiError>(['client-partner'], () => getClientPartner(), {
    refetchOnWindowFocus: false,
    enabled: false,
  });
  const { mutate } = useMutation({
    mutationFn: getNextCloudUrlInstructions,
    onSuccess: (data) => {
      if (data?.status && data?.url) {
        window.open(data.url, '_blank');
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const openNC = async () => {
    mutate();
  };

  useEffect(() => {
    if (authStore?.user?.osv.type === 'Client' && authStore?.user?.osv.WOId) query.refetch();
  }, [authStore?.user, query]);

  if (!authStore.isInitialized) {
    return (
      <Box>
        <Box
          component="main"
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </Box>
    );
  }

  if (!authStore.getIsAuthenticated()) {
    return <Navigate to="/auth/login" />;
  }
  const auditorNavbarItems: NavbarItem[] = [
    {
      title: t('Audit invitations', { defaultValue: 'Audit invitations' }),
      path: '/audit-invitations',
      isActive: location.pathname.includes('/audit-invitations'),
      showNotification: doesUserHaveAnyAuditInvitations,
      color: 'primary',
    },
    {
      title: t('Visits', { defaultValue: 'Visits' }),
      path: '/auditor-visits',
      isActive: location.pathname.includes('/auditor-visits'),
      color: 'primary',
    },
    {
      title: t('Visit report reviews', { defaultValue: 'Visit report reviews' }),
      path: '/audit-visit-rep-review',
      isActive: location.pathname.includes('/audit-visit-rep-review'),
      showNotification: doesUserHaveAnyVisitRepReviews,
      color: 'primary',
    },
    // {
    //   title: t('Reminders', { defaultValue: 'Reminders' }),
    //   path: '/reminders',
    //   isActive: location.pathname.includes('/reminders'),
    //   showNotification: doesUserHaveAnyReminders,
    //   color: 'primary',
    // },
    {
      title: t('Valid registrations', { defaultValue: 'Valid registrations' }),
      path: '/valid-registrations',
      isActive: location.pathname.includes('/valid-registrations'),
      color: 'primary',
    },
    {
      title: t('Auditor proposals', { defaultValue: 'Auditor proposals' }),
      path: '/auditor-proposals',
      isActive: location.pathname.includes('/auditor-proposals'),
      showNotification: doesUserHaveAnyProposals,
      color: 'primary',
    },
    {
      title: t('Competences', { defaultValue: 'Competences' }),
      path: '/auditor-competences',
      isActive: location.pathname.includes('/auditor-competences'),
      showNotification: doesUserHaveAnyCompetences,
      color: 'primary',
    },
  ];

  const clientNavbarItems: NavbarItem[] = [
    {
      title: t('Companies', { defaultValue: 'Companies' }),
      path: '/client-work-orders',
      isActive: location.pathname.includes('/client-work-orders'),
      color: 'primary',
    },
    {
      title: t('Visits', { defaultValue: 'Visits' }),
      path: '/client-visits',
      isActive: location.pathname.includes('/client-visits'),
      color: 'primary',
    },
    {
      title: t('Certificates', { defaultValue: 'Certificates' }),
      path: '/client-certificates',
      isActive: location.pathname.includes('/client-certificates'),
      color: 'primary',
    },
  ];

  return (
    <AuthenticatedLayoutWithNavbar
      menuItems={(authStore.user?.osv.type === 'Auditor'
        ? auditorNavbarItems
        : clientNavbarItems
      ).map((navbarItem) => ({
        title: navbarItem.title,
        onClick: () => navigate(navbarItem.path),
        isActive: navbarItem.isActive,
        showNotification: navbarItem.showNotification,
        color: navbarItem.color,
      }))}
      navbar={
        <AuthenticatedLayoutWithNavbar.Navbar
          logo={
            <Box
              onClick={() => navigate('/')}
              sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
            >
              <Box>
                <img
                  src="/img/SIQ_logo_blue.jpg"
                  alt="logo"
                  style={{ maxWidth: '5rem', objectFit: 'contain' }}
                />
              </Box>
              {authStore.user?.osv.type === 'Client' && query.data && (
                <Typography color="primary">{query.data}</Typography>
              )}
            </Box>
          }
          profileMenu={
            <AuthenticatedLayoutWithNavbar.Navbar.Profile>
              <AuthenticatedLayoutWithNavbar.Navbar.ProfileInfo
                title={authStore.user?.osv.contactNotes || ''}
                subtitle={authStore.user?.user.email || ''}
                onClick={() => {
                  if (authStore.user?.osv.type === 'Auditor') {
                    navigate('/audit-survey');
                  }
                }}
              />
              <AuthenticatedLayoutWithNavbar.Navbar.ProfileDivider />
              {authStore.user?.osv.type === 'Auditor' ? (
                <>
                  <AuthenticatedLayoutWithNavbar.Navbar.ProfileInfo
                    title="povprečna ocena"
                    subtitle={averageRate.grade.toLocaleString()}
                  />
                  <AuthenticatedLayoutWithNavbar.Navbar.ProfileDivider />
                </>
              ) : (
                <> </>
              )}
              <AuthenticatedLayoutWithNavbar.Navbar.ProfileItem
                title={t('Instructions', { defaultValue: 'Instructions' })}
                onClick={openNC}
              />
              <AuthenticatedLayoutWithNavbar.Navbar.ProfileDivider />
              <AuthenticatedLayoutWithNavbar.Navbar.ProfileItem
                title={t('Settings', { defaultValue: 'Settings' })}
                onClick={() => navigate('/user-settings')}
              />
              <AuthenticatedLayoutWithNavbar.Navbar.ProfileDivider />
              <AuthenticatedLayoutWithNavbar.Navbar.ProfileItem
                title={t('Logout', { defaultValue: 'Logout' })}
                onClick={() => {
                  impl.logout();
                  authStore.unauthenticateUser();
                }}
              />
            </AuthenticatedLayoutWithNavbar.Navbar.Profile>
          }
        />
      }
    >
      <Box sx={{ padding: '2rem' }}>
        <Outlet />
      </Box>
    </AuthenticatedLayoutWithNavbar>
  );
};

export default AuthenticatedLayout;
